/**
 * Preview API types for the book preview page.
 * Used by both client (preview page) and server (preview endpoints).
 */
import { z } from 'zod';
import { PURCHASE_TYPE_OPTIONS } from './storyGenerationQuestions.js';
import { lastRegenFailureSchema } from './regeneration.js';

/**
 * Stripe address schema for preview response.
 * Mirrors the StripeAddress interface from order.ts.
 */
const stripeAddressSchema = z.object({
  city: z.string().nullable(),
  country: z.string().nullable(),
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postal_code: z.string().nullable(),
  state: z.string().nullable(),
  name: z.string().nullable().optional(),
});

/**
 * Preview page data returned from GET /api/orders/:id/preview.
 * Contains signed URLs for all generated artifacts.
 */
export const previewPageDataSchema = z.object({
  /** Signed URLs for each composite page image, ordered by page number */
  pageUrls: z.array(z.string().url()),
  /** Signed URL for the 3D cover preview image */
  coverPreviewUrl: z.string().url(),
  /** Signed URL for the digital PDF (null for legacy orders without digital PDF) */
  digitalPdfUrl: z.string().url().nullable(),
  /** Signed URL for the full book PDF (null for digital-only orders) */
  bookPdfUrl: z.string().url().nullable(),
  /** Signed URL for the printable cover PDF (null for digital-only orders) */
  coverPdfUrl: z.string().url().nullable(),
  /** Purchase type determines which actions are available */
  purchaseType: z.enum(PURCHASE_TYPE_OPTIONS),
  /** Shipping address for printed orders (null for digital-only) */
  shippingAddress: stripeAddressSchema.nullable(),
  /** Book title from the approved plan (used for download filename) */
  bookTitle: z.string(),
  /** Simplified print job status derived from Lulu API status */
  printJobStatus: z
    .enum(['none', 'submitted', 'in_production', 'shipped', 'failed'])
    .nullable(),
  /** Lulu tracking URL for shipped orders */
  luluTrackingUrl: z.string().url().nullable().optional(),
  /** Number of cover regenerations remaining (COVER_REGEN_LIMIT - count) */
  coverRegenerationsRemaining: z.number().int().min(0),
  /** Number of book regenerations remaining (BOOK_REGEN_LIMIT - count) */
  bookRegenerationsRemaining: z.number().int().min(0),
  /** Number of single-page regenerations remaining (PAGE_REGEN_LIMIT - count) */
  pageRegenerationsRemaining: z.number().int().min(0),
  /** Plot summary from story outline (for regeneration confirmation modal) */
  plotSummary: z.string(),
  /** Signed URL for the character sheet image (for regeneration confirmation modal) */
  characterSheetImageUrl: z.string().url().nullable(),
  /** Whether the order is locked for printing (buttons disabled) */
  isLockedForPrinting: z.boolean(),
  /**
   * Failure metadata from the most recent regeneration attempt that was
   * restored from backup (issue #500). Surfaces the dismissible banner on
   * the preview page. Null/undefined when there is no pending regen
   * failure to display.
   */
  lastRegenFailure: lastRegenFailureSchema.nullable().optional(),
});

export type PreviewPageData = z.infer<typeof previewPageDataSchema>;

/**
 * Request body for POST /api/orders/:id/verify-code
 */
export const verifyCodeRequestSchema = z.object({
  /** 6-digit numeric verification code sent to email */
  code: z.string().regex(/^\d{6}$/),
});

export type VerifyCodeRequest = z.infer<typeof verifyCodeRequestSchema>;

/**
 * Response from POST /api/orders/:id/verify-code
 */
export const verifyCodeResponseSchema = z.object({
  success: z.boolean(),
});

export type VerifyCodeResponse = z.infer<typeof verifyCodeResponseSchema>;

/**
 * Response from POST /api/orders/:id/send-code
 */
export const sendCodeResponseSchema = z.object({
  success: z.boolean(),
  /** Masked email address the code was sent to (e.g. "j***n@gmail.com") */
  maskedEmail: z.string().optional(),
});

export type SendCodeResponse = z.infer<typeof sendCodeResponseSchema>;

/**
 * Response from GET /api/orders/:id/auth-check
 */
export const authCheckResponseSchema = z.object({
  authenticated: z.boolean(),
  /** True when an admin_token query param was present but invalid/expired */
  adminTokenPresent: z.boolean().optional(),
});

export type AuthCheckResponse = z.infer<typeof authCheckResponseSchema>;

/**
 * Response from GET /api/orders/:id/confirmation?admin_token=xxx
 * Returns order metadata and survey data for the confirmation page.
 */
export const confirmationPageDataSchema = z.object({
  /** Order ID */
  orderId: z.string(),
  /** Current order status */
  status: z.string(),
  /** Customer email address */
  email: z.string().nullable(),
  /** Order creation timestamp */
  createdAt: z.string(),
  /** Full survey JSON data */
  surveyJson: z.record(z.string(), z.unknown()),
});

export type ConfirmationPageData = z.infer<typeof confirmationPageDataSchema>;
